import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@shared/components/ui/alert-dialog'
import { X } from 'lucide-react'
import { useRef, useState, type FC } from 'react'
import {
  STAGE_CONFIG,
  STATUS_STYLES,
  avgScore,
  getCategoryStyle,
  scoreColor,
  type Bet,
  type BetStatus,
  type DragItem,
  type ProofPoint,
  type Stage,
} from '../domain/Bet.domain'
import { useDeleteBet } from '../repositories/useDeleteBet'
import { useGetCategories } from '../repositories/useGetCategories'
import { useUpdateBet } from '../repositories/useUpdateBet'
import { PipelineStage } from './PipelineStage'
import { ScoreIndicator } from './ScoreIndicator'

const STATUSES: BetStatus[] = [
  'Doubling down',
  'Evaluating',
  'Exploring',
  'Pivoting',
  'Killing',
]

interface Props {
  bet: Bet
  isOpen: boolean
  onToggle: () => void
  onChangeProof: (stage: Stage, proofId: string, updated: ProofPoint) => void
  onMoveProof: (item: DragItem, toStage: Stage) => void
}

const STAGES: Stage[] = ['delivered', 'inProgress', 'upNext']

export const BetCard: FC<Props> = ({
  bet,
  isOpen,
  onToggle,
  onChangeProof,
  onMoveProof,
}) => {
  const { mutate: deleteBet, isPending: isDeleting } = useDeleteBet()
  const { mutate: updateBet } = useUpdateBet()
  const { data: categories = [] } = useGetCategories()
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [isEditingThesis, setIsEditingThesis] = useState(false)
  const [editThesis, setEditThesis] = useState(bet.thesis)
  const thesisInputRef = useRef<HTMLInputElement>(null)

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over) return
    const fromStage = active.data.current?.fromStage as Stage | undefined
    const toStage = over.data.current?.stage as Stage | undefined
    if (!fromStage || !toStage || fromStage === toStage) return
    const dragItem: DragItem = {
      type: 'PROOF_POINT',
      proofId: active.data.current?.proofId as string,
      betId: active.data.current?.betId as string,
      fromStage,
    }
    onMoveProof(dragItem, toStage)
  }
  const cs = getCategoryStyle(bet.category.name)
  const ss = STATUS_STYLES[bet.status] ?? { bg: '#f3f4f6', fg: '#374151' }
  const allPts = [...bet.delivered, ...bet.inProgress, ...bet.upNext]
  const avg = allPts.length ? avgScore(allPts) : 0

  const dPct = allPts.length ? (bet.delivered.length / allPts.length) * 100 : 0
  const iPct = allPts.length ? (bet.inProgress.length / allPts.length) * 100 : 0
  const uPct = allPts.length ? (bet.upNext.length / allPts.length) * 100 : 0

  return (
    <div
      style={{
        border: '1px solid #e5e7eb',
        borderRadius: 12,
        background: '#fff',
        overflow: 'hidden',
        boxShadow: isOpen ? '0 2px 8px rgba(0,0,0,0.06)' : 'none',
      }}
    >
      {/* Header */}
      <div
        onClick={onToggle}
        style={{ padding: '14px 20px', cursor: 'pointer' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <ScoreIndicator score={avg} size={20} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flexWrap: 'wrap',
              }}
            >
              <span
                style={{ fontSize: 15, fontWeight: 600, cursor: 'text' }}
                onClick={(e) => {
                  e.stopPropagation()
                  setIsEditingThesis(true)
                  setEditThesis(bet.thesis)
                  setTimeout(() => thesisInputRef.current?.focus(), 0)
                }}
              >
                {isEditingThesis ? (
                  <input
                    ref={thesisInputRef}
                    value={editThesis}
                    onChange={(e) => setEditThesis(e.target.value)}
                    onBlur={() => {
                      setIsEditingThesis(false)
                      const trimmed = editThesis.trim()
                      if (trimmed && trimmed !== bet.thesis) {
                        updateBet({ id: bet.id, thesis: trimmed })
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        ;(e.target as HTMLInputElement).blur()
                      }
                      if (e.key === 'Escape') {
                        setEditThesis(bet.thesis)
                        setIsEditingThesis(false)
                      }
                    }}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      fontSize: 15,
                      fontWeight: 600,
                      border: '1px solid #d1d5db',
                      borderRadius: 6,
                      padding: '1px 6px',
                      outline: 'none',
                      width: '100%',
                      background: '#fff',
                    }}
                  />
                ) : (
                  bet.thesis
                )}
              </span>
              <select
                value={bet.category.id}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => {
                  updateBet({
                    id: bet.id,
                    categoryId: e.target.value,
                  })
                }}
                style={{
                  fontSize: 11,
                  padding: '2px 8px',
                  borderRadius: 10,
                  background: cs.bg,
                  color: cs.fg,
                  fontWeight: 500,
                  border: 'none',
                  cursor: 'pointer',
                  appearance: 'none',
                  WebkitAppearance: 'none',
                }}
              >
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 3 }}>
              {bet.owner.name} · avg score {avg.toFixed(1)}
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              flexShrink: 0,
            }}
          >
            <div style={{ display: 'flex', gap: 3 }}>
              {allPts.map((p) => (
                <div
                  key={p.id}
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 2,
                    background: scoreColor(p.score),
                  }}
                />
              ))}
            </div>
            <select
              value={bet.status}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => {
                updateBet({
                  id: bet.id,
                  status: e.target.value as BetStatus,
                })
              }}
              style={{
                fontSize: 12,
                padding: '3px 10px',
                borderRadius: 8,
                background: ss.bg,
                color: ss.fg,
                fontWeight: 600,
                whiteSpace: 'nowrap',
                border: 'none',
                cursor: 'pointer',
                appearance: 'none',
                WebkitAppearance: 'none',
              }}
            >
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <button
              onClick={(e) => {
                e.stopPropagation()
                setShowDeleteDialog(true)
              }}
              disabled={isDeleting}
              title="Delete bet"
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#d1d5db',
                padding: '4px',
                lineHeight: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <X size={16} />
            </button>
            <span
              style={{
                fontSize: 11,
                color: '#9ca3af',
                transform: isOpen ? 'rotate(90deg)' : 'rotate(0)',
                transition: 'transform 0.15s',
              }}
            >
              ▶
            </span>
          </div>
        </div>
        {/* Progress bar */}
        <div
          style={{
            marginTop: 8,
            display: 'flex',
            height: 4,
            borderRadius: 2,
            overflow: 'hidden',
            background: '#f3f4f6',
          }}
        >
          {dPct > 0 && (
            <div
              style={{
                width: `${dPct}%`,
                background: STAGE_CONFIG.delivered.accent,
              }}
            />
          )}
          {iPct > 0 && (
            <div
              style={{
                width: `${iPct}%`,
                background: STAGE_CONFIG.inProgress.accent,
              }}
            />
          )}
          {uPct > 0 && (
            <div
              style={{
                width: `${uPct}%`,
                background: '#ddd6fe',
              }}
            />
          )}
        </div>
      </div>

      {/* Expanded pipeline */}
      {isOpen && (
        <div
          style={{
            padding: '0 20px 20px 20px',
            borderTop: '1px solid #f3f4f6',
          }}
        >
          <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
            <div style={{ display: 'flex', gap: 16, marginTop: 16 }}>
              {STAGES.map((stage) => (
                <PipelineStage
                  key={stage}
                  stage={stage}
                  betId={bet.id}
                  items={bet[stage]}
                  onChangeItem={(pid, updated) =>
                    onChangeProof(stage, pid, updated)
                  }
                />
              ))}
            </div>
          </DndContext>
        </div>
      )}

      {/* Delete confirmation dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete bet</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete &quot;{bet.thesis}&quot; and all its
              proof points. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel variant="outline" size="default">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              variant="default"
              size="default"
              onClick={() => deleteBet(bet.id)}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
